import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('fec_token'));
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem('fec_token');
    setToken(null);
    setUser(null);
  }, []);

  // Verify token on mount — only logout on auth errors, not network timeouts
  useEffect(() => {
    if (!token) { setLoading(false); return; }

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);

    fetch(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    })
      .then(res => {
        clearTimeout(timeout);
        if (res.status === 401 || res.status === 403) {
          // Genuine auth failure — logout
          logout();
          return null;
        }
        if (!res.ok) {
          // Server error (500, 502, etc.) — keep token, try again later
          console.warn('Auth check failed with status:', res.status);
          return null;
        }
        return res.json();
      })
      .then(data => {
        if (data) setUser(data);
      })
      .catch((err) => {
        clearTimeout(timeout);
        if (err.name === 'AbortError') {
          console.warn('Auth check timed out — server busy, keeping session');
        } else {
          console.warn('Auth check network error — keeping session', err.message);
        }
        // On network errors or timeouts, DON'T logout — just set user from token
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          setUser({ id: payload.sub, email: payload.email, role: payload.role, name: payload.email });
        } catch (e) {
          // Malformed token — logout
          logout();
        }
      })
      .finally(() => setLoading(false));

    return () => { clearTimeout(timeout); controller.abort(); };
  }, [token, logout]);

  const login = async (email, password, rememberMe = false) => {
    const res = await fetch(`${API}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, remember_me: rememberMe }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Login failed');
    }
    const data = await res.json();
    localStorage.setItem('fec_token', data.token);
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const getAuthHeaders = useCallback(() => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [token]);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, getAuthHeaders }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
